import { useEffect, useState } from "react";
import { api, formatBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, DollarSign, AlertCircle, Award, MessageCircle, LifeBuoy, Sparkles, Zap } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLAN_COLORS = { solo: "#94A3B8", dupla: "#D4651A", equipe: "#1B3A2F" };

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/dashboard?period=${period}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return (
    <div className="p-6 lg:p-10 space-y-4">
      <Skeleton className="h-10 w-72" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="admin-dashboard-root">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-1">Painel do gestor</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A2F]">Visão geral do negócio</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-stone-500 hidden sm:block">
            {data?.comparison && (
              <span>
                Cadastros: <strong className={data.comparison.signups_delta_pct >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {data.comparison.signups_delta_pct >= 0 ? '+' : ''}{data.comparison.signups_delta_pct}%
                </strong> vs período anterior
              </span>
            )}
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="admin-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
              <SelectItem value="90d">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Revenue + Users top row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="MRR" value={formatBRL(data.revenue.mrr)} sub={`Ticket médio ${formatBRL(data.revenue.ticket_medio)}`} icon={DollarSign} accent />
        <Stat label="Usuários" value={data.users.total} sub={`${data.users.active_7d} ativos em 7d`} icon={Users} />
        <Stat label="Em trial" value={data.users.in_trial} sub={`Conversão ${data.conversion.trial_pct}%`} icon={Sparkles} />
        <Stat label="Retenção" value={`${data.churn.retention_rate_pct}%`} sub={`${data.churn.cancelled_30d} canc. em 30d`} icon={TrendingUp} warn={data.churn.retention_rate_pct < 90} />
      </div>

      {/* MRR by plan + Signups chart */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-6 rounded-2xl lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-lg font-bold">Novos cadastros ({period === '7d' ? '7' : period === '30d' ? '30' : '90'} dias)</h2>
              <p className="text-xs text-stone-500">Crescimento de base</p>
            </div>
            <Badge variant="outline">Total: {data.signups_chart.reduce((a, b) => a + b.count, 0)}</Badge>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.signups_chart}>
                <CartesianGrid stroke="#e7e5e4" strokeDasharray="3 3" />
                <XAxis dataKey="day" hide />
                <YAxis allowDecimals={false} stroke="#a8a29e" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "#1B3A2F", border: "none", borderRadius: 8, color: "#fff" }}
                  labelFormatter={(d) => new Date(d).toLocaleDateString("pt-BR")}
                />
                <Line type="monotone" dataKey="count" stroke="#D4651A" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <h2 className="font-heading text-lg font-bold mb-3">MRR por plano</h2>
          <div className="space-y-4">
            {Object.entries(data.revenue.mrr_by_plan).map(([plan, value]) => {
              const pct = data.revenue.mrr ? (value / data.revenue.mrr) * 100 : 0;
              return (
                <div key={plan}>
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="text-sm font-semibold capitalize">{plan}</span>
                    <span className="text-sm font-bold">{formatBRL(value)}</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: PLAN_COLORS[plan] }} />
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">{data.users.by_plan[plan]} assinantes · {pct.toFixed(0)}%</div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Engagement + AI cost */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Leads gerenciados" value={data.engagement.total_leads.toLocaleString("pt-BR")} icon={Users} small />
        <Stat label="Imóveis cadastrados" value={data.engagement.total_properties.toLocaleString("pt-BR")} icon={Award} small />
        <Stat label="Mensagens com IA" value={data.engagement.ai_messages.toLocaleString("pt-BR")} icon={MessageCircle} small />
        <Stat label="Custo estimado IA" value={`US$ ${data.ai_cost.est_cost_usd}`} sub={`${(data.ai_cost.tokens_in / 1000).toFixed(0)}k tokens in`} icon={Zap} small />
      </div>

      {/* Top corretores */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6 rounded-2xl">
          <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-[#D4651A]" /> Top corretores · vendas fechadas
          </h2>
          {data.top_closers.length === 0 ? (
            <p className="text-sm text-stone-500">Nenhum fechamento registrado ainda.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_closers.map((c, i) => (
                <li key={c._id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#D4651A] text-white' : 'bg-stone-200 text-stone-600'}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{c.name}</div>
                    <div className="text-xs text-stone-500 truncate">{c.email}</div>
                  </div>
                  <Badge className="bg-[#1B3A2F] text-white hover:bg-[#1B3A2F]">{c.count} venda{c.count > 1 ? 's' : ''}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6 rounded-2xl">
          <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#1B3A2F]" /> Top corretores · carteira ativa
          </h2>
          {data.top_active.length === 0 ? (
            <p className="text-sm text-stone-500">Sem dados ainda.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_active.map((c, i) => (
                <li key={c._id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50">
                  <div className="w-7 h-7 rounded-full bg-stone-100 text-stone-600 flex items-center justify-center text-xs font-bold">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{c.name}</div>
                    <div className="text-xs text-stone-500 truncate">{c.email}</div>
                  </div>
                  <Badge variant="outline">{c.count} leads</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Tickets summary */}
      <Card className="p-6 rounded-2xl">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <LifeBuoy className="w-4 h-4 text-[#D4651A]" /> Suporte
            </h2>
            <p className="text-xs text-stone-500">Pedidos abertos pelos corretores</p>
          </div>
          <Badge variant="outline" className="text-sm">
            Tempo médio de resposta: <strong className="ml-1">{data.tickets.avg_response_hours}h</strong>
          </Badge>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <TicketStat label="Abertos" value={data.tickets.open} color="bg-amber-100 text-amber-700" testid="ticket-stat-open" />
          <TicketStat label="Respondidos" value={data.tickets.responded} color="bg-blue-100 text-blue-700" testid="ticket-stat-responded" />
          <TicketStat label="Fechados" value={data.tickets.closed} color="bg-green-100 text-green-700" testid="ticket-stat-closed" />
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, accent, warn, small, testid }) {
  return (
    <Card
      data-testid={testid}
      className={`p-5 rounded-2xl transition hover:-translate-y-0.5 hover:shadow-md ${
        accent ? "bg-[#1B3A2F] text-white border-[#1B3A2F]" : warn ? "border-amber-300 bg-amber-50" : "bg-white"
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className={`text-[11px] uppercase tracking-wider font-bold ${accent ? "text-white/70" : "text-stone-500"}`}>{label}</div>
        <Icon className={`w-4 h-4 ${accent ? "text-[#D4651A]" : warn ? "text-amber-600" : "text-stone-400"}`} />
      </div>
      <div className={`font-heading ${small ? 'text-2xl' : 'text-3xl'} font-extrabold ${accent ? "text-white" : "text-[#1C1C19]"} mt-1`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/60" : "text-stone-500"}`}>{sub}</div>}
    </Card>
  );
}

function TicketStat({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg p-3 ${color}`}>
      <div className="text-xs font-semibold opacity-80">{label}</div>
      <div className="font-heading text-2xl font-extrabold">{value}</div>
    </div>
  );
}
